import { useFieldArray, useFormContext } from 'react-hook-form';
import { PlusIcon } from 'lucide-react';

import { SectionContainer } from 'components/DetailsView/Section';
import { DetailFormValue } from 'components/DetailsView';
import { TotemTrashButton } from 'components/TotemTrashButton';
import { Button } from 'components/ui/button';
import { UltraSelectField } from 'components/UltraSelect';

import { categorySource } from 'data/entity-sources/sources';

import type { MealPlanDefinitionFormData } from '../constants/formData';

/**
 * Un EMPLACEMENT de la formule : « Plat », « Boisson »… et les catégories de produits qui peuvent
 * l'occuper. Deux règles portées par l'API, rappelées ici parce qu'elles décident de la saisie :
 * il en faut au moins deux (une formule est une remise combinée), et une même catégorie ne peut pas
 * figurer dans deux emplacements — sinon l'affectation d'un produit dépendrait de l'ordre de saisie.
 */
export const EligibleCategoriesSection = () => {
    const { register } = useFormContext<MealPlanDefinitionFormData>();
    const { fields, append, remove } = useFieldArray<MealPlanDefinitionFormData>({ name: 'eligibleCategories' });

    return (
        <SectionContainer title="Emplacements" isInitiallyOpen>
            <div className="flex flex-col gap-3">
                <span className="text-[13px] text-muted-foreground">
                    L'ordre compte : un produit est affecté au premier emplacement qui l'accepte. Une formule a besoin
                    d'au moins deux emplacements, et une catégorie ne peut pas figurer dans deux d'entre eux.
                </span>
                {fields.map((field, index) => (
                    <div key={field.id} className="flex items-end gap-3 rounded-lg border border-border p-3">
                        <div className="w-64">
                            <DetailFormValue
                                label={`Emplacement ${index + 1}`}
                                placeholder="Plat, Boisson, Dessert…"
                                data-test={`meal-plan-definition-spot-label-${index}`}
                                {...register(`eligibleCategories.${index}.label`, { required: true })}
                            />
                        </div>
                        <div className="flex-1">
                            <UltraSelectField
                                source={categorySource}
                                label="Catégories acceptées"
                                name={`eligibleCategories.${index}.categoryIds`}
                                dataTest={`meal-plan-definition-spot-categories-${index}`}
                                multiple
                                required
                            />
                        </div>
                        <TotemTrashButton onClick={() => remove(index)} disabled={fields.length <= 2} />
                    </div>
                ))}
                <div>
                    <Button
                        type="button"
                        size="icon-sm"
                        onClick={() => append({ label: '', categoryIds: [] })}
                        data-test="meal-plan-definition-add-spot"
                    >
                        <PlusIcon />
                    </Button>
                </div>
            </div>
        </SectionContainer>
    );
};
